import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export interface CartItem {
  typeId: number; // 1 = event, 2 = custom
  eventId?: number;
  eventTitle?: string;
  eventPrice?: number;
  eventDate?: Date;
  eventBranches?: { name: string; address: string }[]; // nombres de las sucursales del evento (solo UI)
  selections?: { branchId: number; categoryId: number }[]; // solo IDs, sin datos de UI
  branchNames?: string[]; // nombres de sucursales del recorrido (solo UI)
  quantity: number;
  visitorNames: string[];
  pricePerUnit: number;
}

@Injectable({
  providedIn: 'root'
})
export class CartService {
  private readonly CART_STORAGE_KEY = 'beer_cart';
  private cartItems$ = new BehaviorSubject<CartItem[]>(this.loadCart());

  constructor() {
    // Escuchar cambios en localStorage desde otras pestañas
    window.addEventListener('storage', (event) => {
      if (event.key === this.CART_STORAGE_KEY) {
        this.cartItems$.next(this.loadCart());
      }
    });
  }

  private sanitizeName(name: string): string {
    if (typeof name !== 'string') return '';
    const stripped = name.replace(/[^a-zA-ZáéíóúÁÉÍÓÚñÑ ]/g, '');
    const firstSpace = stripped.indexOf(' ');
    return firstSpace === -1
      ? stripped
      : stripped.slice(0, firstSpace + 1) + stripped.slice(firstSpace + 1).replace(/ /g, '');
  }

  private sanitizeCart(cart: CartItem[]): CartItem[] {
    if (!Array.isArray(cart)) return [];
    return cart.map(item => ({
      ...item,
      visitorNames: Array.isArray(item.visitorNames)
        ? item.visitorNames.map(n => this.sanitizeName(n))
        : [],
      eventBranches: Array.isArray(item.eventBranches)
        ? item.eventBranches.map((b: any) =>
            typeof b === 'string'
              ? { name: b, address: '' }          // migrate old string format
              : { name: b.name ?? '', address: b.address ?? '' }
          )
        : undefined
    }));
  }

  private loadCart(): CartItem[] {
    try {
      const cartData = localStorage.getItem(this.CART_STORAGE_KEY);
      const parsed = cartData ? JSON.parse(cartData) : [];
      const sanitized = this.sanitizeCart(parsed);
      // Write sanitized version back so localStorage stays clean
      localStorage.setItem(this.CART_STORAGE_KEY, JSON.stringify(sanitized));
      return sanitized;
    } catch (error) {
      console.error('Error loading cart from localStorage', error);
      return [];
    }
  }

  private saveCart(cart: CartItem[]): void {
    try {
      localStorage.setItem(this.CART_STORAGE_KEY, JSON.stringify(cart));
      this.cartItems$.next(cart);
    } catch (error) {
      console.error('Error saving cart to localStorage', error);
    }
  }

  getCart(): Observable<CartItem[]> {
    return this.cartItems$.asObservable();
  }

  getCartSnapshot(): CartItem[] {
    return this.cartItems$.getValue();
  }

  addEvent(eventData: any, quantity: number = 1): void {
    const cart = this.getCartSnapshot();
    
    // Buscar si ya existe este evento en el carrito
    const existingItemIndex = cart.findIndex(
      item => item.typeId === 1 && item.eventId === eventData.id
    );

    if (existingItemIndex !== -1) {
      // Incrementar cantidad del item existente
      cart[existingItemIndex].quantity += quantity;
      // Inicializar visitor names vacíos para las nuevas cantidades
      for (let i = 0; i < quantity; i++) {
        cart[existingItemIndex].visitorNames.push('');
      }
    } else {
      // Agregar nuevo item
      const newItem: CartItem = {
        typeId: 1,
        eventId: eventData.id,
        eventTitle: eventData.title,
        eventPrice: eventData.price,
        eventDate: eventData.date,
        eventBranches: (eventData.eventBranches ?? []).map((b: any) => ({
          name: b.branch?.brewery?.name ?? '',
          address: b.branch?.address ?? ''
        })).filter((b: any) => b.name || b.address),
        quantity: quantity,
        visitorNames: Array(quantity).fill(''),
        pricePerUnit: Math.max(eventData.price - (eventData.discountAmount || 0), 0)
      };
      cart.push(newItem);
    }

    this.saveCart(cart);
  }

  addCustomRoute(selections: any[], quantity: number = 1, pricePerUnit: number): void {
    const cart = this.getCartSnapshot();
    
    // Separar los nombres de las selecciones (los nombres son solo para UI)
    const cleanSelections = selections.map(({ branchId, categoryId }: any) => ({ branchId, categoryId }));
    const branchNames: string[] = selections.map((s: any) => s.branchName).filter(Boolean);

    // Comparar selecciones: mismo conjunto de branchId+categoryId (sin importar orden)
    const toKey = (sels: { branchId: number; categoryId: number }[]) =>
      [...sels].map(s => `${s.branchId}-${s.categoryId}`).sort().join('|');
    const newKey = toKey(cleanSelections);

    const existingIndex = cart.findIndex(
      item => item.typeId === 2 && toKey(item.selections ?? []) === newKey
    );

    if (existingIndex !== -1) {
      // Incrementar cantidad del item existente
      const item = cart[existingIndex];
      item.quantity += quantity;
      for (let i = 0; i < quantity; i++) {
        item.visitorNames.push('');
      }
    } else {
      // Agregar nuevo item con selecciones limpias
      const newItem: CartItem = {
        typeId: 2,
        selections: cleanSelections,
        branchNames: branchNames,
        quantity: quantity,
        visitorNames: Array(quantity).fill(''),
        pricePerUnit: pricePerUnit
      };
      cart.push(newItem);
    }
    
    this.saveCart(cart);
  }

  updateQuantity(index: number, newQuantity: number): void {
    if (newQuantity < 1) {
      return;
    }

    const cart = this.getCartSnapshot();
    if (index >= 0 && index < cart.length) {
      const item = cart[index];
      const oldQuantity = item.quantity;
      
      if (newQuantity > oldQuantity) {
        // Agregar visitor names vacíos
        for (let i = 0; i < (newQuantity - oldQuantity); i++) {
          item.visitorNames.push('');
        }
      } else if (newQuantity < oldQuantity) {
        // Eliminar visitor names excedentes
        item.visitorNames = item.visitorNames.slice(0, newQuantity);
      }
      
      item.quantity = newQuantity;
      this.saveCart(cart);
    }
  }

  updateVisitorNames(index: number, visitorNames: string[]): void {
    const cart = JSON.parse(JSON.stringify(this.getCartSnapshot())); // Deep copy
    if (index >= 0 && index < cart.length) {
      cart[index].visitorNames = visitorNames.map(n => this.sanitizeName(n));
      this.saveCart(cart);
    }
  }

  removeItem(index: number): void {
    const cart = this.getCartSnapshot();
    if (index >= 0 && index < cart.length) {
      cart.splice(index, 1);
      this.saveCart(cart);
    }
  }

  clearCart(): void {
    this.saveCart([]);
  }

  getTotal(): number {
    return this.getCartSnapshot().reduce((total, item) => {
      const subtotal = item.pricePerUnit * item.quantity;
      return total + (subtotal * 1.07); // Include 7% tax
    }, 0);
  }

  getItemCount(): number {
    return this.getCartSnapshot().reduce((count, item) => count + item.quantity, 0);
  }

  isCartValid(): boolean {
    const cart = this.getCartSnapshot();
    
    if (cart.length === 0) {
      return false;
    }

    // Validar que todos los visitor names estén llenos
    return cart.every(item => 
      item.visitorNames.length === item.quantity &&
      item.visitorNames.every(name => name && name.trim().length > 0)
    );
  }
}
